// ============================================
// FILE: /frontend/src/components/dashboard/ListingDetailsModal.jsx
// PURPOSE: Mobile-first modal showing full listing details with actions
// ============================================

import React from 'react';
import { X, Edit, Trash2, Pause, Play, Star, Eye, MessageSquare, MapPin, Calendar } from 'lucide-react';

export default function ListingDetailsModal({ 
  isOpen, 
  onClose, 
  listing, 
  onEdit,
  onDelete,
  onSuspend,
  onReactivate,
  onPromote,
  loading,
  getStatusBadge
}) {
  const API_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:5000';

  if (!isOpen || !listing) return null;

  // Get primary image
  const getImageUrl = () => {
    let imageUrl = listing.imageUrl;
    
    // Handle array format (new schema)
    if (Array.isArray(imageUrl) && imageUrl.length > 0) {
      imageUrl = imageUrl[0];
    }
    
    // Fallback to photos array
    if (!imageUrl && listing.photos && listing.photos.length > 0) {
      imageUrl = listing.photos[0];
    }
    
    // Check if it's a string before using .startsWith()
    if (imageUrl && typeof imageUrl === 'string') {
      return imageUrl.startsWith('http') 
        ? imageUrl 
        : `${API_BASE_URL}${imageUrl}`;
    }
    
    return '/placeholder-car.jpg';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header - responsive padding */}
        <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-base sm:text-lg md:text-xl font-bold truncate pr-2">
            Listing Details
          </h2>
          <button 
            onClick={onClose} 
            className="p-1 hover:bg-gray-100 rounded-md transition flex-shrink-0"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Content - responsive padding */}
        <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          
          {/* Image - responsive aspect ratio */}
          <div className="relative aspect-video sm:aspect-[16/10] rounded-lg overflow-hidden bg-muted">
            <img
              src={getImageUrl()}
              alt={listing.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/placeholder-car.jpg';
              }}
            />
            {listing.isFeatured && (
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-yellow-500 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-md text-xs sm:text-sm font-semibold flex items-center gap-1 shadow-lg">
                <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-current" />
                Featured
              </div>
            )}
          </div>

          {/* Title and Status - responsive text */}
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold leading-tight flex-1">
                {listing.title}
              </h3>
              <span className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium flex-shrink-0 ${getStatusBadge(listing.status)}`}>
                {listing.status}
              </span>
            </div>
            
            {/* Price - responsive text */}
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">
              KES {listing.price?.toLocaleString() || '0'}
            </p>
          </div>

          {/* Stats - responsive layout and text */}
          <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5 sm:gap-2">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="font-medium">{listing.viewsCount || 0}</span>
              <span className="hidden sm:inline">views</span>
            </span>
            <span className="flex items-center gap-1.5 sm:gap-2">
              <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="font-medium">{listing.inquiriesCount || 0}</span>
              <span className="hidden sm:inline">inquiries</span>
            </span>
            {listing.county && (
              <span className="flex items-center gap-1.5 sm:gap-2">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">{listing.county}</span>
              </span>
            )}
            <span className="flex items-center gap-1.5 sm:gap-2">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="hidden sm:inline">Posted</span>
              <span>{new Date(listing.createdAt).toLocaleDateString()}</span>
            </span>
          </div>

          {/* Description - responsive text */}
          {listing.description && (
            <div className="space-y-1.5 sm:space-y-2">
              <h4 className="text-sm sm:text-base font-semibold">Description</h4>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {listing.description}
              </p>
            </div>
          )}

          {/* Additional Info - responsive grid */}
          {(listing.type || listing.subType || listing.make || listing.model) && (
            <div className="space-y-1.5 sm:space-y-2">
              <h4 className="text-sm sm:text-base font-semibold">Details</h4>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                {listing.type && (
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <span className="ml-2 font-medium">{listing.type}</span>
                  </div>
                )}
                {listing.subType && (
                  <div>
                    <span className="text-muted-foreground">Sub-type:</span>
                    <span className="ml-2 font-medium">{listing.subType}</span>
                  </div>
                )}
                {listing.make && (
                  <div>
                    <span className="text-muted-foreground">Make:</span>
                    <span className="ml-2 font-medium">{listing.make}</span>
                  </div>
                )}
                {listing.model && (
                  <div>
                    <span className="text-muted-foreground">Model:</span>
                    <span className="ml-2 font-medium">{listing.model}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions - responsive layout */}
          <div className="border-t pt-4 sm:pt-6 space-y-3">
            <h4 className="text-sm sm:text-base font-semibold">Actions</h4>
            
            {/* Primary Actions - responsive grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <button 
                onClick={() => {
                  onEdit(listing.id);
                  onClose();
                }}
                className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-md transition text-xs sm:text-sm font-medium h-9 sm:h-10"
              >
                <Edit className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span>Edit</span>
              </button>

              {listing.status === 'APPROVED' && (
                <button 
                  onClick={() => {
                    onSuspend(listing.id);
                    onClose();
                  }}
                  disabled={loading === listing.id}
                  className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-md transition text-xs sm:text-sm font-medium disabled:opacity-50 h-9 sm:h-10"
                >
                  <Pause className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>Suspend</span>
                </button>
              )}

              {listing.status === 'SUSPENDED' && (
                <button 
                  onClick={() => {
                    onReactivate(listing.id);
                    onClose();
                  }}
                  disabled={loading === listing.id}
                  className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-green-50 hover:bg-green-100 text-green-600 rounded-md transition text-xs sm:text-sm font-medium disabled:opacity-50 h-9 sm:h-10"
                >
                  <Play className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>Reactivate</span>
                </button>
              )}

              <button 
                onClick={() => {
                  if (window.confirm(`Delete "${listing.title}"? This cannot be undone.`)) {
                    onDelete(listing.id, listing.title);
                    onClose();
                  }
                }}
                disabled={loading === listing.id}
                className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-md transition text-xs sm:text-sm font-medium disabled:opacity-50 h-9 sm:h-10"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span>Delete</span>
              </button>
            </div>

            {/* Promote Button - full width if available */}
            {listing.status === 'APPROVED' && !listing.isFeatured && (
              <button 
                onClick={() => {
                  onPromote(listing);
                  onClose();
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md transition text-sm sm:text-base font-semibold h-10 sm:h-11"
              >
                <Star className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                Promote to Featured
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}